// KI-Regelset-Vorschlag fuer Dokumente ohne passendes Regelset (status no_ruleset): startet
// einen Job im Backend (POST /documents/:id/suggest-ruleset) und pollt das Ergebnis alle 4 s
// (max. 15 Min, Timeout danach). 409 (bereits ein Job fuer dieses Dokument) und 503
// (LLM_MODE=off/unkonfiguriert) werden dediziert behandelt. Eigene Datei, damit
// AbDocumentDetail.js nicht weiter waechst (500-LOC-Regel, "Feature => eigene Datei").

import React, { useEffect, useRef, useState } from 'react';
import { Sparkles, Loader2, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';
import { Card, SectionTitle, Chip, btnPrimary, money, qty, fmtDate } from './ui';
import { startSuggest, getSuggestJob, listSuggestJobs, applySuggest } from '../api';

const POLL_INTERVAL_MS = 4000;
const MAX_POLL_MS = 15 * 60 * 1000;
const PREVIEW_FIELDS = ['ebeln', 'ebelp', 'matnr', 'menge', 'netpr', 'eeind'];

const AbSuggestPanel = ({ user, isDarkMode, t, documentId, isAdmin, showSuccess, showError }) => {
  const [jobId, setJobId] = useState(null);
  const [phase, setPhase] = useState('idle'); // idle | starting | running | done | error | not_configured
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [applying, setApplying] = useState(false);
  const startedAtRef = useRef(null);
  const timerRef = useRef(null);

  // Reload-Sicherheit: pruefen, ob fuer dieses Dokument bereits ein Job laeuft, und daran anknuepfen.
  useEffect(() => {
    let alive = true;
    listSuggestJobs(user)
      .then((jobs) => {
        if (!alive) return;
        const running = (Array.isArray(jobs) ? jobs : []).find((j) => j.documentId === documentId && j.status === 'running');
        if (running) {
          startedAtRef.current = Date.now();
          setJobId(running.jobId);
          setPhase('running');
        }
      })
      .catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId]);

  // Polling mit Cleanup-Return (RULES: Timer im useEffect-Cleanup abmelden).
  useEffect(() => {
    if (phase !== 'running' || !jobId) return undefined;
    let stopped = false;
    const poll = async () => {
      if (stopped) return;
      if (Date.now() - startedAtRef.current > MAX_POLL_MS) {
        setErrorMsg(t('ab.suggestTimeout'));
        setPhase('error');
        return;
      }
      try {
        const res = await getSuggestJob(user, jobId);
        if (stopped) return;
        if (res?.status === 'done') {
          setResult(res.result || null);
          setPhase('done');
        } else if (res?.status === 'error') {
          setErrorMsg(res?.error || t('ab.suggestFailed'));
          setPhase('error');
        } else {
          timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
        }
      } catch (e) {
        if (stopped) return;
        setErrorMsg(e.message);
        setPhase('error');
      }
    };
    poll();
    return () => { stopped = true; if (timerRef.current) clearTimeout(timerRef.current); };
  }, [phase, jobId, user, t]);

  const start = async () => {
    setPhase('starting');
    setErrorMsg(null);
    setResult(null);
    try {
      const res = await startSuggest(user, documentId);
      startedAtRef.current = Date.now();
      setJobId(res.jobId);
      setPhase('running');
    } catch (e) {
      if (e.status === 503) {
        setPhase('not_configured');
      } else if (e.status === 409) {
        setErrorMsg(t('ab.suggestAlreadyRunning'));
        setPhase('error');
      } else {
        setErrorMsg(e.message);
        setPhase('error');
      }
    }
  };

  const apply = async () => {
    if (!jobId) return;
    setApplying(true);
    try {
      await applySuggest(user, jobId);
      showSuccess?.(t('ab.suggestAppliedMsg'));
    } catch (e) {
      showError?.(e.message);
    } finally {
      setApplying(false);
    }
  };

  const textMuted = isDarkMode ? 'text-white/55' : 'text-slate-500';

  return (
    <Card isDarkMode={isDarkMode} className={isDarkMode ? 'border-indigo-400/20' : 'border-indigo-200'}>
      <SectionTitle isDarkMode={isDarkMode}>
        <span className="flex items-center gap-2"><Sparkles className="w-4 h-4" />{t('ab.suggestTitle')}</span>
      </SectionTitle>

      {phase === 'idle' && (
        <>
          <p className={`text-sm mb-3 ${textMuted}`}>{t('ab.suggestDesc')}</p>
          {isAdmin ? (
            <button onClick={start} className={btnPrimary(isDarkMode)}>
              <span className="flex items-center gap-2"><Sparkles className="w-4 h-4" />{t('ab.suggestButton')}</span>
            </button>
          ) : (
            // Start ist serverseitig Admins vorbehalten (LLM-Kosten) — Nutzern den Weg zeigen statt 403.
            <p className={`text-sm ${textMuted}`}>{t('ab.suggestAdminOnly')}</p>
          )}
        </>
      )}

      {phase === 'starting' && (
        <p className={`text-sm flex items-center gap-2 ${textMuted}`}><Loader2 className="w-4 h-4 animate-spin" />{t('ab.suggestStarting')}</p>
      )}

      {phase === 'running' && (
        <p className={`text-sm flex items-center gap-2 ${textMuted}`}><Loader2 className="w-4 h-4 animate-spin" />{t('ab.suggestRunning')}</p>
      )}

      {phase === 'not_configured' && (
        <p className={`text-sm flex items-start gap-2 ${isDarkMode ? 'text-amber-300' : 'text-amber-600'}`}>
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />{t('ab.suggestNotConfigured')}
        </p>
      )}

      {phase === 'error' && (
        <div className="space-y-2">
          <p className={`text-sm flex items-start gap-2 ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}>
            <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />{errorMsg}
          </p>
          <button onClick={start} className={btnPrimary(isDarkMode)}>{t('ab.suggestRetryButton')}</button>
        </div>
      )}

      {phase === 'done' && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Chip tone={result?.matched ? 'green' : 'amber'} isDarkMode={isDarkMode}>
              <span className="inline-flex items-center gap-1">
                {result?.matched && <CheckCircle2 className="w-3 h-3" />}
                {t('ab.suggestResultSummary', { lineCount: result?.lineCount ?? 0 })}
              </span>
            </Chip>
          </div>
          {Array.isArray(result?.lines) && result.lines.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-xs">
                <thead>
                  <tr className={isDarkMode ? 'border-b border-white/10' : 'border-b border-slate-200'}>
                    {PREVIEW_FIELDS.map((f) => (
                      <th key={f} className={`text-left px-2 py-1.5 uppercase tracking-wide font-semibold ${textMuted}`}>{t(`ab.field.${f}`)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.lines.slice(0, 20).map((l, i) => (
                    <tr key={i} className={isDarkMode ? 'border-b border-white/5' : 'border-b border-slate-100'}>
                      <td className="px-2 py-1.5 font-mono">{l.ebeln || '—'}</td>
                      <td className="px-2 py-1.5 font-mono">{l.ebelp || '—'}</td>
                      <td className="px-2 py-1.5">{l.matnr || '—'}</td>
                      <td className="px-2 py-1.5 tabular-nums">{qty(l.menge)}</td>
                      <td className="px-2 py-1.5 tabular-nums">{money(l.netpr, l.waers)}</td>
                      <td className="px-2 py-1.5">{fmtDate(l.eeind)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {isAdmin && (
            <div className="pt-1">
              <button onClick={apply} disabled={applying} className={btnPrimary(isDarkMode)}>
                {applying ? t('common.saving') : t('ab.suggestApplyButton')}
              </button>
              <p className={`text-xs mt-2 ${textMuted}`}>{t('ab.suggestApplyHint')}</p>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

export default AbSuggestPanel;
